// @fileoverview : 영업관리 > 수주항목
import { mutatePurchaseOrderItems } from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { users } from "@/api/users/useUsersQuery";
import { itemTypeColor } from "@/constants/items";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/DetailForm";
import { ProductionPlanCreateForm } from "@/features/productionPlan/form/CreateForm";
import { PurchaseOrderDetailForm } from "@/features/purchaseOrder/form/DetailForm";
import { PurchaseOrderOutgoingForm } from "@/features/purchaseOrder/purchaseOrderOutgoingForm";
import { DeliveredForm } from "@/features/purchaseOrderItems/delivered/DeliveredForm";
import { PurchaseOrderItemsCreateForm } from "@/features/purchaseOrderItems/form/CreateForm";
import { PurchaseOrderItemsDetailForm } from "@/features/purchaseOrderItems/form/PurchaseOrderItemDetailForm";
import { PurchaseOrderItemsUpdateForm } from "@/features/purchaseOrderItems/form/UpdateForm";
import { ProductionPlanForm } from "@/features/purchaseOrderItems/productionPlan/ProductionPlanForm";
import { StockPlanForm } from "@/features/purchaseOrderItems/stockPlan/StockPlanForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/Alert";
import { ConfirmForm } from "@/features/ui/confirm";
import DatetimeUtil from "@/utils/dateTimeUtil";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { purchaseOrderItemStatusColor, setPurchaseOrderItemStatus } from "@/utils/scmDataStatus";
import timeUtil from "@/utils/timeUtil";
import { setToLocaleString } from "@/utils/unitMark";
import { ActionIcon, Avatar, Badge, Button, Flex, Text, Tooltip } from "@mantine/core";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  PartnersGet200ResponseRowsInnerTypeEnum,
  PurchaseOrderItemsGet200Response,
  PurchaseOrderItemsWithItemGradeGet200ResponseRowsInner,
  ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import {
  IconEdit,
  IconFilterPlus,
  IconPlus,
  IconRecycle,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const PurchaseOrderItemsPage = () => {
  const { t } = useTranslation();
  const { data: userInfo } = useQuery(users.who({}));
  const userCode = userInfo?.data?.code;
  const partnerType = userInfo?.data?.partner?.type;

  const { openModal, closeModal } = useModal();
  const { query, setQuery, selectedRows, refetch, resetQueryStrings, data } = useStandardLayout();

  const selectedData = (data as AxiosResponse<PurchaseOrderItemsGet200Response, any>)?.data?.rows
    ?.filter((item) => selectedRows.has(item.id))
    ?.map((item) => item);

  useEffect(() => {
    if (query.length === 0) {
      // query가 비어 있을 때만 실행
      setQuery([{ direction: { $eq: "INBOUND" } }]);
    }

    if (userInfo?.data?.externalUserType) {
      setQuery([{ partnerCode: { $eq: userInfo?.data?.partner?.code } }]);
    }
  }, [query, userCode]); // 의존성 배열 유지

  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest) =>
      mutatePurchaseOrderItems
        .delete(params)
        .mutationFn(params as ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          t("message.deleteSuccess", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          `${t("common.deleteSuccess")}`,
          "green",
        );
        closeModal(null);
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: translateWithPartnerType(
              t,
              "purchaseOrderItems.purchaseOrderItems",
              partnerType,
            ),
          }),
          t("common.deleteFail", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          "red",
        );
      },
    },
  );

  const columns: readonly Column<AuthSignupPost201ResponseEquipmentPurchaseOrderItem>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "partner.name",
      name: t("partner.name"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <PartnerDetailForm partnerCode={formatterProps?.row.partnerCode} />,
                null,
                t("common.detail", { item: t("partner.partner") }),
              )
            }
          >
            {formatterProps.row.partner?.name}
          </LinkAnchor>
        );
      },
    },
    {
      key: "purchaseOrderCode",
      name: translateWithPartnerType(t, "purchaseOrder.code", partnerType),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex w="100%" h="100%">
            <LinkAnchor
              onClick={() =>
                openModal(
                  <PurchaseOrderDetailForm
                    purchaseOrderId={formatterProps?.row.purchaseOrderId ?? 0}
                  />,
                  null,
                  t("common.detail", {
                    item: translateWithPartnerType(
                      t,
                      "purchaseOrderItems.purchaseOrderItems",
                      partnerType,
                    ),
                  }),
                )
              }
            >
              {formatterProps.row.purchaseOrderCode}
            </LinkAnchor>
          </Flex>
        );
      },
    },
    {
      key: "itemType",
      name: "품목군",
      sortable: true,
      resizable: true,
      formatter: ({ row }: { row: PurchaseOrderItemsWithItemGradeGet200ResponseRowsInner }) => {
        return (
          <Badge size="lg" color={itemTypeColor[row?.itemType as keyof typeof itemTypeColor]}>
            {t(row?.itemType as string)}
          </Badge>
        );
      },
    },
    {
      key: "itemName",
      name: "품목명",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <ItemsDetailForm itemCode={formatterProps?.row?.itemCode} />,
                null,
                "품목 상세",
              )
            }
          >
            {formatterProps.row.itemName}
          </LinkAnchor>
        );
      },
    },
    {
      key: "gradeName",
      name: "Grade",
      sortable: true,
      resizable: true,
    },
    {
      key: "itemCategoryName",
      name: "Color",
      sortable: true,
      resizable: true,
    },
    {
      key: "abstractItemCode",
      name: "형태",
      sortable: true,
      resizable: true,
    },
    {
      key: "status",
      name: translateWithPartnerType(t, "purchaseOrderItems.status", partnerType),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Badge
            variant="filled"
            color={
              purchaseOrderItemStatusColor[
                formatterProps.row.status as keyof typeof purchaseOrderItemStatusColor
              ]
            }
          >
            {setPurchaseOrderItemStatus(formatterProps.row.status ?? "")}
          </Badge>
        );
      },
    },
    {
      key: "quantity",
      name: t("inventory.quantity"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return <Flex justify="flex-end">{setToLocaleString(formatterProps.row.quantity)}</Flex>;
      },
    },
    {
      key: "sumOfProductionPlanQuantity",
      name: t("purchaseOrderItems.sumOfProductionPlanQuantity"),
      sortable: true,
      resizable: true,

      formatter: (formatterProps) => {
        return (
          <Flex justify="flex-end" align="center" h="100%">
            {formatterProps.row.sumOfProductionPlanQuantity === "0" ? (
              <div>{formatterProps.row.sumOfProductionPlanQuantity}</div>
            ) : (
              <LinkAnchor
                onClick={() =>
                  openModal(
                    <ProductionPlanForm purchaseOrderItemID={formatterProps?.row?.id} />,
                    null,
                    t("common.detail", { item: t("productionPlan.productionPlan") }),
                  )
                }
              >
                {setToLocaleString(formatterProps.row.sumOfProductionPlanQuantity)}
              </LinkAnchor>
            )}
          </Flex>
        );
      },
    },
    {
      key: "sumOfStockPlanQuantity",
      name: translateWithPartnerType(t, "purchaseOrderItems.sumOfStockPlanQuantity", partnerType),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex h="100%" justify="flex-end" align="center">
            <LinkAnchor
              onClick={() => {
                openModal(
                  <StockPlanForm purchaseOrderItemID={formatterProps?.row?.id} />,
                  null,
                  t("common.detail", { item: t("stockPlan.stockPlan") }),
                );
              }}
            >
              {setToLocaleString(formatterProps.row.sumOfStockPlanQuantity)}
            </LinkAnchor>
          </Flex>
        );
      },
    },
    {
      key: "sumOfDeliveredQuantity",
      name: translateWithPartnerType(t, "purchaseOrderItems.sumOfDeliveredQuantity", partnerType),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex h="100%" justify="flex-end" align="center">
            {formatterProps.row.sumOfDeliveredQuantity === "0" ? (
              <Text>{formatterProps.row.sumOfDeliveredQuantity}</Text>
            ) : (
              <LinkAnchor
                onClick={() =>
                  openModal(
                    <DeliveredForm purchaseOrderItemID={formatterProps?.row?.id} />,
                    null,
                    t("common.detail", { item: t("delivery.deliveryComplete") }),
                  )
                }
              >
                {setToLocaleString(formatterProps.row.sumOfDeliveredQuantity)}
              </LinkAnchor>
            )}
          </Flex>
        );
      },
    },
    {
      key: "orderDate",
      name: translateWithPartnerType(t, "purchaseOrder.orderDate", partnerType),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex>
            {formatterProps.row.orderDate !== null
              ? DatetimeUtil(formatterProps.row.orderDate as string)
              : ""}
          </Flex>
        );
      },
    },
    {
      key: "createdAt",
      name: t("order.createdAt"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex>
            {formatterProps.row.createdAt !== null
              ? DatetimeUtil(formatterProps.row.createdAt as string)
              : ""}
          </Flex>
        );
      },
    },
    {
      key: "deliveryDeadline",
      name: t("order.deliveryDate"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex>
            {formatterProps.row.deliveryDeadline !== null
              ? timeUtil(formatterProps.row.deliveryDeadline as string)
              : ""}
          </Flex>
        );
      },
    },
    {
      key: "options",
      name: t("common.action"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const detailPurchaseOrderItemActionView = () => {
          openModal(
            <PurchaseOrderItemsDetailForm
              purchaseOrderItemId={formatterProps.row.id as number}
              pageKey="purchaseOrderItems"
            />,
            null,
            t("common.detail", {
              item: translateWithPartnerType(
                t,
                "purchaseOrderItems.purchaseOrderItems",
                partnerType,
              ),
            }),
          );
        };
        const updatePurchaseOrderItemActionView = () => {
          openModal(
            <PurchaseOrderItemsUpdateForm formatterProps={formatterProps.row} />,
            null,
            t("common.edit", {
              item: translateWithPartnerType(
                t,
                "purchaseOrderItems.purchaseOrderItems",
                partnerType,
              ),
            }),
          );
        };

        const deletePurchaseOrderItemAction = () => {
          openModal(
            <ConfirmForm
              message={t("message.rowDelete")}
              yesCallback={() => {
                deleteMutate({ purchaseOrderItemId: formatterProps?.row?.id as number });
              }}
              noCallback={() => {
                closeModal(null);
              }}
            />,
            null,
            t("common.delete", {
              item: translateWithPartnerType(
                t,
                "purchaseOrderItems.purchaseOrderItems",
                partnerType,
              ),
            }),
          );
        };

        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon onClick={detailPurchaseOrderItemActionView} variant="subtle" color="blue">
              <IconSearch />
            </ActionIcon>
            <ActionIcon variant="subtle" onClick={updatePurchaseOrderItemActionView} color="yellow">
              <IconEdit />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={() => deletePurchaseOrderItemAction()}
              color="red"
            >
              <IconTrash />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        t("message.deleteSelect", {
          item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
        }),
        t("common.deleteFail", {
          item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
        }),
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ purchaseOrderItemId: row });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      t("common.delete", {
        item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
      }),
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <Flex gap="sm">
          {partnerType === PartnersGet200ResponseRowsInnerTypeEnum.SALES ? null : (
            <>
              <Button
                leftIcon={<IconPlus />}
                onClick={() =>
                  openModal(
                    <PurchaseOrderItemsCreateForm />,
                    null,
                    t("common.create", {
                      item: translateWithPartnerType(
                        t,
                        "purchaseOrderItems.purchaseOrderItems",
                        partnerType,
                      ),
                    }),
                  )
                }
              >
                {t("common.create", {
                  item: translateWithPartnerType(
                    t,
                    "purchaseOrderItems.purchaseOrderItems",
                    partnerType,
                  ),
                })}
              </Button>
              <Button
                leftIcon={<IconPlus />}
                color="orange"
                disabled={
                  [...selectedRows].length == 0 ||
                  selectedData?.some((item) => item.status === "DELIVERED")
                }
                onClick={() => {
                  openModal(
                    <PurchaseOrderOutgoingForm purchaseOrderItems={selectedData} />,
                    null,
                    t("delivery.action"),
                  );
                }}
              >
                {t("delivery.action")}
              </Button>
              <Button
                leftIcon={<IconPlus />}
                color="orange"
                disabled={[...selectedRows].length !== 1}
                onClick={() =>
                  openModal(
                    <ProductionPlanCreateForm
                      formatterProps={
                        selectedData &&
                        (selectedData[0] as AuthSignupPost201ResponseEquipmentPurchaseOrderItem)
                      }
                    />,
                    null,
                    t("common.create", { item: t("productionPlan.productionPlan") }),
                    true,
                  )
                }
              >
                {t("common.create", { item: t("productionPlan.productionPlan") })}
              </Button>
            </>
          )}
        </Flex>
        <Flex gap="sm">
          <Button rightIcon={<IconRecycle />} color="teal" onClick={() => resetQueryStrings()}>
            {t("common.reset")}
          </Button>
          <Button rightIcon={<IconTrash />} color="red" onClick={() => handleDelete()}>
            {t("common.rowDelete")}
          </Button>
        </Flex>
      </Flex>
      <Flex px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>
          {t("common.list", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          })}
        </Text>
        <Text fz="xs">
          {t("common.subTitle", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          })}{" "}
          ({t("purchaseOrderItems.customer")})
        </Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<
            PurchaseOrderItemsGet200Response,
            AuthSignupPost201ResponseEquipmentPurchaseOrderItem
          >
            columns={columns}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<PurchaseOrderItemsGet200Response> />
      </Flex>
    </Flex>
  );
};
